/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Cookies } from "react-cookie";

import UserSlice, { UserSliceFromRoot } from "types/UserSlice";

import axios from "connection";

import { fetchConfigFromAPI, redirectToID, setLoader, setModal } from "state/config-slice";
import getOpco from "utils/getOpco";
import { setDebugInfo } from "utils/debugHelper";

import { API_URL, PPE_V1_PATH, PPE_V2_PATH } from "config";
import { isFeatureActive } from "utils/getFeature";
import CableContract from "types/CableContract";
import { CustomerType } from "types/Customer";

const PATH = isFeatureActive("PPE 1.0 Backend") ? JSON.parse(PPE_V1_PATH)["v2"] : PPE_V2_PATH;
const PATH_LOGOUT = isFeatureActive("PPE 1.0 Backend") ? "" : PPE_V2_PATH;

const initialState = {
  loaded: false,
  loggedIn: false,
  userInfo: {},
  choosenChargingId: null,
  mustSelectContract: false,
} as UserSlice;

export const fetchUserFromAPI = createAsyncThunk("user/fetchUser", async (_arg, { dispatch }: any) => {
  try {
    const response = await axios.get(`${API_URL}${PATH}/me?opco=${getOpco()}&marketplace=true`);

    if (response.status === 200) {
      const userInfo = response.data;
      setDebugInfo("userInfo", userInfo);
      return { loggedIn: true, userInfo, loaded: true };
    }

    return { loggedIn: false, userInfo: {}, loaded: true };
  } catch (error: any) {
    if (error?.response?.status === 401) {
      dispatch(setLoader({ showLoader: true, messageHeader: "", messageText: "" }));
      dispatch(redirectToID(null));

      const cookies = new Cookies();
      cookies.remove("CART", { path: "/" });

      return { loggedIn: false, userInfo: {}, loaded: true };
    }

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

function htmlDecode(input: string) {
  const doc = new DOMParser().parseFromString(input, "text/html");

  return doc.documentElement.textContent?.replace("#", "") || "";
}

export const logoutUserFromAPI = createAsyncThunk("user/logoutUser", async (_arg, { getState, dispatch }: any) => {
  const csrfToken: string = getState().user.userInfo.csrfToken;

  try {
    dispatch(setLoader({ showLoader: true }));

    const result = await axios.post(
      `${API_URL}${PATH_LOGOUT}/logout?opco=${getOpco()}&marketplace=true`,
      { opco: getOpco(), msisdn: getState().user.choosenChargingId?.chargingId },
      {
        headers: {
          "X-vfgcsrftoken": csrfToken,
        },
      },
    );

    if (result.status === 200) {
      dispatch(fetchConfigFromAPI());
      const redirectURLfromID = result.data;

      return {
        loggedIn: false,
        redirectUrl: isFeatureActive("PPE 1.0 Backend") ? `${redirectURLfromID}` : `${htmlDecode(redirectURLfromID)}`,
      };
    }

    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    return { loggedIn: true };
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
    return Promise.reject(error);
  }
});

// will be continued
export const selectContractAPI = createAsyncThunk(
  "user/selectContract",
  async (accountContract: CableContract, { dispatch }: any) => {
    try {
      dispatch(setLoader({ showLoader: true }));
      const response = await axios.post(`${API_URL}${PATH}/account/selector`, {
        ...accountContract,
      });

      if (response.status === 200) {
        dispatch(setLoader({ showLoader: false }));
        return { contractSelectedSuccess: true };
      }
    } catch (error: any) {
      dispatch(setLoader({ showLoader: false }));
      return { contractSelectedSuccess: false };
    }
  },
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    selectCharginID: (state, action) => {
      return {
        ...state,
        choosenChargingId: { ...action.payload },
      };
    },
    setMustSelectContract: (state, action) => {
      return {
        ...state,
        mustSelectContract: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserFromAPI.fulfilled, (state, action) => {
        state.userInfo = action.payload.userInfo;
        state.choosenChargingId =
          action.payload.userInfo.chargingIds && action.payload.userInfo.chargingIds.length
            ? action.payload.userInfo.chargingIds[0]
            : null;

        state.loaded = true;
        state.loggedIn = action.payload.loggedIn;
        if (state.choosenChargingId) state.userInfo.customerType = state.choosenChargingId.type;
        else if (state.userInfo.chargingId) {
          state.userInfo.customerType = state.userInfo.chargingId.type;
        }

        state.mustSelectContract = [
          CustomerType.broadband as string,
          CustomerType.can as string,
          CustomerType.uan as string,
          CustomerType.tv as string,
        ].includes(state.userInfo.customerType?.toUpperCase());
      })
      .addCase(fetchUserFromAPI.rejected, (state) => {
        state.loaded = true;
      })
      .addCase(logoutUserFromAPI.pending, (state) => {
        state.loaded = false;
        state.loggedIn = true;
      })
      .addCase(selectContractAPI.fulfilled, (state) => {
        state.mustSelectContract = false;
      })
      .addCase(logoutUserFromAPI.fulfilled, () => {
        return { ...initialState };
      });
  },
});

export const { selectCharginID, setMustSelectContract } = userSlice.actions;

export const userState = (state: UserSliceFromRoot): UserSlice => state.user;

export const fetchedUser =
  (): any =>
  (state: UserSliceFromRoot): any => {
    return state?.user?.userInfo || null;
  };

export default userSlice.reducer;
